import type { ReactElement } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button';
import Tooltip from '@atlaskit/tooltip';

export type QuickActionCommonProps = {
	// Admin announcement banner id
	id: string;
};

export enum QuickActionName {
	COPY = 'copy',
	EDIT = 'edit',
	ARCHIVE = 'archive',
}

type QuickActionBaseProps = {
	icon: ReactElement;
	onClick: () => void;
	actionName: QuickActionName;
};

type QuickActionBaseComponentProps = Pick<QuickActionBaseProps, 'icon' | 'actionName'> &
	Partial<Pick<QuickActionBaseProps, 'onClick'>>;

const i18n = defineMessages({
	edit: {
		id: 'admin-announcement-banner.table.quick-actions.edit',
		defaultMessage: 'Edit',
		description: 'This is the tooltip text for the edit announcement banner button.',
	},
	copy: {
		id: 'admin-announcement-banner.table.quick-actions.copy',
		defaultMessage: 'Copy',
		description: 'This is the tooltip text for the copy announcement banner button.',
	},
	archive: {
		id: 'admin-announcement-banner.table.quick-actions.archive',
		defaultMessage: 'Archive',
		description: 'This is the tooltip text for the archive announcement banner button.',
	},
});

export const QuickActionBaseComponent = ({
	icon,
	onClick,
	actionName,
}: QuickActionBaseComponentProps) => {
	const intl = useIntl();

	const quickActionLabel = intl.formatMessage(i18n[actionName]);

	return (
		<Tooltip content={quickActionLabel}>
			{(tooltipProps) => (
				<Button
					{...tooltipProps}
					aria-label={quickActionLabel}
					appearance="subtle"
					iconBefore={icon}
					onClick={onClick}
				/>
			)}
		</Tooltip>
	);
};
